import './accounts.css';
import { useEffect, useState, type FormEvent } from 'react';
import { api, errorText } from '../../api';
import { ConfirmDialog, Dialog } from '../../components/Dialog';
import { useConfig } from '../../lib/config';
import { initials } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { useToast } from '../../lib/toast';
import { AREA_LABEL, type StaffArea } from '../../types';
import { AREAS, staffLabel } from './labels';
import { LinkDialog } from './LinkDialog';
import type { LinkResult, TeamData } from './types';

type Staff = TeamData['staff'][number];
type Client = TeamData['clients'][number];

const AREA_OPTS: StaffArea[] = ['fibu', 'lohn', 'beides'];

type EmailResult = LinkResult & { invited: boolean };

// Team & Zuweisung (nur Admin; andere leitet das Routing auf die Übersicht um).
export function TeamPage() {
  const team = useApi<TeamData>('/api/kanzlei/team', { live: ['team', 'clients'] });
  const toast = useToast();
  const config = useConfig();
  // Link zum Weitergeben, wenn kein E-Mail-Versand eingerichtet ist
  const [invite, setInvite] = useState<{ link: string; name: string; email: string } | null>(null);
  const [editing, setEditing] = useState<Staff | null>(null);
  const [removing, setRemoving] = useState<Staff | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = 'Team & Zuweisung · Elbcons Kanzlei';
  }, []);

  const staff = team.data?.staff ?? [];
  const clients = team.data?.clients ?? [];

  function linkResult(res: LinkResult, name: string, email: string, sentText: string) {
    if (res.sent) toast(sentText);
    else if (res.link) setInvite({ link: res.link, name, email });
  }

  async function resend(s: Staff) {
    try {
      const res = await api.post<LinkResult>(`/api/kanzlei/team/${s.id}/resend`);
      linkResult(res, s.name, s.email, `Einladung an ${s.name} (${s.email}) erneut verschickt`);
    } catch (err) {
      toast(errorText(err));
    }
  }

  async function remove(s: Staff) {
    setBusy(true);
    try {
      const res = await api.del<{ fibuCount: number; lohnCount: number }>(`/api/kanzlei/team/${s.id}`);
      setRemoving(null);
      toast(
        `${s.name} entfernt` +
          (res.fibuCount + res.lohnCount ? ` · bitte unten neu zuweisen (FiBu: ${res.fibuCount} · Lohn: ${res.lohnCount})` : ''),
      );
      await team.reload();
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="acc">
      <div className="head">
        <div>
          <h1>Team &amp; Zuweisung</h1>
          <p>Legen Sie fest, welcher Buchhalter welche Mandanten sieht.</p>
        </div>
      </div>
      <div className="lock lc-note" role="status">
        <span aria-hidden="true">🔒</span>
        <span>
          {config.loginCode
            ? 'Login-Code per E-Mail ist an: Jeder in der Kanzlei bekommt bei der Anmeldung einen Code per E-Mail. Mandanten können ihn in der App selbst einschalten.'
            : config.mailEnabled
              ? 'Login-Code per E-Mail ist ausgeschaltet (LOGIN_CODE=aus auf dem Server).'
              : 'Login-Code per E-Mail ist noch aus, weil kein E-Mail-Versand eingerichtet ist. Danach braucht jeder in der Kanzlei bei der Anmeldung einen Code aus der E-Mail.'}
        </span>
      </div>
      {!team.data ? (
        team.error ? (
          <p className="note">{team.error}</p>
        ) : (
          <div className="spinner" aria-label="Lädt" />
        )
      ) : (
        <>
          <div className="kpis team">
            {staff.map((s) => (
              <StaffCard key={s.id} s={s} onResend={() => resend(s)} onEmail={() => setEditing(s)} onRemove={() => setRemoving(s)} />
            ))}
            <InviteCard
              onDone={(res, name, email) => {
                linkResult(res, name, email, `Einladung an ${name} (${email}) verschickt · nach der Anmeldung unten Mandanten zuweisen`);
                void team.reload();
              }}
            />
          </div>
          <UstvaSenden staff={staff} ich={team.data.ich} reload={team.reload} />
          <Assignments staff={staff} clients={clients} reload={team.reload} />
        </>
      )}
      <LinkDialog
        link={invite?.link ?? null}
        title={invite ? `Einladung für ${invite.name}` : 'Einladung'}
        text={
          invite && (
            <>
              Es ist kein E-Mail-Versand eingerichtet. Bitte schicken Sie diesen Link an {invite.name} ({invite.email}). Darüber
              wird das Passwort festgelegt. Der Link gilt 7 Tage.
            </>
          )
        }
        onClose={() => setInvite(null)}
      />
      {editing && (
        <EmailDialog
          s={editing}
          onClose={() => setEditing(null)}
          onDone={(res, email) => {
            setEditing(null);
            if (res.invited) linkResult(res, editing.name, email, `E-Mail geändert · Einladung an ${editing.name} (${email}) verschickt`);
            else toast(`E-Mail von ${editing.name} geändert`);
            void team.reload();
          }}
        />
      )}
      <ConfirmDialog
        open={!!removing}
        title={removing ? `${removing.name} entfernen?` : ''}
        text={removing && <RemoveText s={removing} />}
        confirmLabel="Ja, entfernen"
        danger
        busy={busy}
        onConfirm={() => removing && void remove(removing)}
        onCancel={() => setRemoving(null)}
      />
    </div>
  );
}

// Wer darf Umsatzsteuer-Voranmeldungen für Mandanten an ELSTER senden? Nur Steuerberater, Bilanzbuchhalter und
// Steuerfachwirte; alle anderen geben die Zahlen dem Mandanten zum Senden.
function UstvaSenden({ staff, ich, reload }: { staff: Staff[]; ich: TeamData['ich']; reload(): Promise<void> }) {
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  // sofort zeigen, was angeklickt wurde (bis die Liste neu geladen ist)
  const [an, setAn] = useState<Record<string, boolean>>({});
  const leute = [{ id: ich.id, name: 'Sie selbst', ustvaSenden: ich.ustvaSenden }, ...staff.filter((s) => s.area !== 'lohn')];
  async function setzen(id: string, name: string, erlaubt: boolean) {
    setBusy(id);
    setAn((a) => ({ ...a, [id]: erlaubt }));
    try {
      await api.put(`/api/kanzlei/team/${id}/ustva-senden`, { erlaubt });
      await reload();
      toast(erlaubt ? `${name}: darf Umsatzsteuer senden` : `${name}: gibt die Umsatzsteuer dem Mandanten`);
    } catch (err) {
      setAn((a) => ({ ...a, [id]: !erlaubt }));
      toast(errorText(err));
    } finally {
      setBusy(null);
    }
  }
  return (
    <div className="card ust-recht">
      <div className="ch">
        <h3>Umsatzsteuer an ELSTER senden</h3>
        <span>Nur für Steuerberater, Bilanzbuchhalter und Steuerfachwirte anhaken. Alle anderen geben die Zahlen dem Mandanten.</span>
      </div>
      {leute.map((p) => (
        <label key={p.id} className="ust-recht-zeile">
          <input type="checkbox" checked={an[p.id] ?? p.ustvaSenden} disabled={busy === p.id} onChange={(e) => void setzen(p.id, p.name, e.target.checked)} />
          <span>{p.name}</span>
        </label>
      ))}
    </div>
  );
}

function RemoveText({ s }: { s: Staff }) {
  const n = s.fibuCount + s.lohnCount;
  return (
    <>
      {s.status === 'invited' ? 'Die Einladung gilt dann nicht mehr. ' : `${s.name} kann sich danach nicht mehr anmelden. `}
      {n > 0 &&
        `Die Mandanten (FiBu: ${s.fibuCount} · Lohn: ${s.lohnCount}) sind dann offen und landen bei Ihnen, bis Sie sie unten neu zuweisen. `}
      Belege, Chats und Protokoll bleiben erhalten.
    </>
  );
}

// E-Mail eines Mitarbeiters ändern (z. B. Tippfehler in der Einladung oder neue Adresse)
function EmailDialog({ s, onClose, onDone }: { s: Staff; onClose(): void; onDone(res: EmailResult, email: string): void }) {
  const toast = useToast();
  const config = useConfig();
  const [email, setEmail] = useState(s.email);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const m = email.trim();
    if (!m) return;
    setBusy(true);
    try {
      onDone(await api.post<EmailResult>(`/api/kanzlei/team/${s.id}/email`, { email: m }), m);
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onClose={onClose} title={`E-Mail von ${s.name} ändern`}>
      <form className="form" onSubmit={(e) => void submit(e)}>
        <label>
          Neue E-Mail
          <input
            type="email"
            required
            maxLength={120}
            autoComplete="off"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <p className="note">
          {s.status === 'invited'
            ? 'Die Einladung geht neu an diese Adresse, der bisherige Link gilt nicht mehr.'
            : `Anmeldung, Login-Code und Passwort-Links gehen danach an die neue Adresse.${config.mailEnabled ? ' Die bisherige Adresse bekommt einen Hinweis.' : ''}`}
        </p>
        <div className="dlg-actions">
          <button type="button" className="btn" onClick={onClose}>
            Abbrechen
          </button>
          <button type="submit" className="btn dark" disabled={busy}>
            Speichern
          </button>
        </div>
      </form>
    </Dialog>
  );
}

function StaffCard({ s, onResend, onEmail, onRemove }: { s: Staff; onResend(): Promise<void>; onEmail(): void; onRemove(): void }) {
  const [busy, setBusy] = useState(false);
  return (
    <div className="card">
      <div className="kl">{AREA_LABEL[s.area ?? 'fibu']}</div>
      <div className="who-row">
        <span className="av" aria-hidden="true">
          {initials(s.name)}
        </span>
        <b>{s.name}</b>
      </div>
      <div className="ks" style={{ marginTop: 6 }}>
        FiBu: {s.fibuCount} · Lohn: {s.lohnCount}
      </div>
      {s.status !== 'invited' && <div className="note invited">{s.email}</div>}
      {s.status === 'invited' && (
        <>
          <div className="note invited">eingeladen · {s.email}</div>
          <button
            type="button"
            className="btn sm"
            disabled={busy}
            aria-label={`Einladung an ${s.name} erneut senden`}
            onClick={() => {
              setBusy(true);
              void onResend().finally(() => setBusy(false));
            }}
          >
            Einladung erneut senden
          </button>
        </>
      )}
      <div className="staff-act">
        <button type="button" className="lnk" onClick={onEmail} aria-label={`E-Mail von ${s.name} ändern`}>
          E-Mail ändern
        </button>
        <button type="button" className="lnk del" onClick={onRemove} aria-label={`${s.name} entfernen`}>
          Entfernen
        </button>
      </div>
    </div>
  );
}

// Karte „Neuer Buchhalter“
function InviteCard({ onDone }: { onDone(res: LinkResult, name: string, email: string): void }) {
  const toast = useToast();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [area, setArea] = useState<StaffArea>('fibu');
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const n = name.trim();
    const m = email.trim();
    if (!n || !m) return;
    setBusy(true);
    try {
      const res = await api.post<LinkResult & { userId: string }>('/api/kanzlei/team/invite', { name: n, email: m, area });
      setName('');
      setEmail('');
      setArea('fibu');
      onDone(res, n, m);
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <div className="kl" id="inv-h">
        Neuer Buchhalter
      </div>
      <form className="form" onSubmit={(e) => void submit(e)} aria-labelledby="inv-h">
        <input
          id="inv-n"
          placeholder="Vor- und Nachname"
          aria-label="Name"
          required
          maxLength={60}
          autoComplete="off"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <input
          id="inv-mail"
          type="email"
          placeholder="E-Mail"
          aria-label="E-Mail"
          required
          maxLength={120}
          autoComplete="off"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <select id="inv-t" aria-label="Bereich" value={area} onChange={(e) => setArea(e.target.value as StaffArea)}>
          {AREA_OPTS.map((a) => (
            <option key={a} value={a}>
              {AREA_LABEL[a]}
            </option>
          ))}
        </select>
        <button className="btn sm dark" type="submit" disabled={busy}>
          Einladen
        </button>
      </form>
    </div>
  );
}

// Karte „Mandanten zuweisen“: pro Mandant FiBu und Lohn, Änderung gilt sofort
function Assignments({ staff, clients, reload }: { staff: Staff[]; clients: Client[]; reload(): Promise<void> }) {
  const toast = useToast();
  // Zuweisbar sind nur Mitarbeiter, die ihren Zugang schon eingerichtet haben
  const options = staff.filter((s) => s.status === 'active');
  const anyAbroad = clients.some((c) => c.country !== 'DE');
  const [busy, setBusy] = useState<string | null>(null);

  async function assign(c: Client, area: 'fibu' | 'lohn', userId: string) {
    setBusy(`${c.id}-${area}`);
    try {
      const res = await api.post<{ name: string | null }>(`/api/kanzlei/clients/${c.id}/assign`, { area, userId: userId || null });
      toast(`${c.name} · ${area === 'fibu' ? 'FiBu' : 'Lohn'} → ${res.name ?? 'nicht zugewiesen'}`);
      await reload();
    } catch (err) {
      toast(errorText(err));
      await reload();
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="card" aria-labelledby="assign-h">
      <div className="ch">
        <h3 id="assign-h">Mandanten zuweisen</h3>
        <span>Pro Mandant ein Buchhalter für FiBu und einer für Lohn · gilt sofort</span>
      </div>
      {clients.length === 0 ? (
        <p className="note empty-note">Noch keine Mandanten.</p>
      ) : (
        <div className="tw">
          <table className="tteam">
            <thead>
              <tr>
                <th>Mandant</th>
                {anyAbroad && <th>Land</th>}
                {AREAS.map((a) => (
                  <th key={a.key}>{a.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.id}>
                  <td>
                    <b>{c.name}</b>
                    <span className="sub">Nr. {c.number}</span>
                  </td>
                  {anyAbroad && <td>{c.country === 'AE' ? <span className="flag f-ae">UAE</span> : c.country}</td>}
                  {AREAS.map((a) => {
                    const value = a.key === 'fibu' ? c.fibuUserId : c.lohnUserId;
                    return (
                      <td key={a.key} data-l={a.label}>
                        <select
                          className={value ? undefined : 'miss'}
                          aria-label={`${a.label} für ${c.name}`}
                          value={value ?? ''}
                          disabled={busy === `${c.id}-${a.key}`}
                          onChange={(e) => void assign(c, a.key, e.target.value)}
                        >
                          <option value="">nicht zugewiesen</option>
                          {options.map((s) => (
                            <option key={s.id} value={s.id}>
                              {staffLabel(s)}
                            </option>
                          ))}
                        </select>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
